import { useState, useEffect } from "react";
import { extractAnswersFromSection } from "@/utils/data-transforms";
import { handleAsync } from "@/utils/error-handling";
import { logger } from "@/utils/logger";

/**
 * Custom hook for managing materiality data state and operations.
 * Handles data loading, saving, and state management for the materiality assessment.
 */
export function useMaterialityData(companyName: string) {
  const [planningData, setPlanningData] = useState<any>(null);
  const [revisionData, setRevisionData] = useState<any>(null);
  const [abcotdData, setAbcotdData] = useState<any>(null);
  const [componentData, setComponentData] = useState<any>(null);
  const [documentationData, setDocumentationData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("planning");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [crossSectionAnswers, setCrossSectionAnswers] = useState<Record<string, any>>({});

  // Load existing materiality data and cross-section answers
  useEffect(() => {
    const loadMaterialityData = async () => {
      try {
        const data = await (window as any).planning.readSection(
          companyName,
          "materiality",
        );
        if (data && data.planningData) {
          setPlanningData(data.planningData);
        }
        if (data && data.revisionData) {
          setRevisionData(data.revisionData);
        }
        if (data && data.abcotdData) {
          setAbcotdData(data.abcotdData);
        }
        if (data && data.componentData) {
          setComponentData(data.componentData);
        }
        if (data && data.documentationData) {
          setDocumentationData(data.documentationData);
        }
      } catch (error) {
        logger.warn("Failed to load materiality data", { error });
      }
    };

    const loadCrossSectionAnswers = async () => {
      try {
        const answers: Record<string, any> = {};

        // Load all potential cross-section dependencies
        const sectionsToLoad = [
          "understanding-entity",
          "fraud-risk",
          "it-risk",
          "preliminary-analytical",
        ];

        // Load regular sections from local storage
        for (const section of sectionsToLoad) {
          try {
            const sectionData = await (window as any).planning.readSection(
              companyName,
              section,
            );

            // Extract answers from section data
            if (sectionData) {
              Object.assign(answers, extractAnswersFromSection(sectionData));
            }
          } catch (sectionError) {
            logger.warn(`Failed to load ${section} for cross-section answers`, {
              error: sectionError,
            });
          }
        }

        // Load Engagement Acceptance from SharePoint
        try {
          if (window.planningAPI?.loadResponses) {
            console.log("Attempting to load from SharePoint...");
            const sharePointResponses = await window.planningAPI.loadResponses({
              companyName,
              sectionKey: "engagement-acceptance"
            });
            
            console.log("SharePoint responses received:", sharePointResponses);
            logger.debug("SharePoint responses received:", sharePointResponses);
            
            if (sharePointResponses && sharePointResponses.success) {
              console.log("Raw SharePoint data structure:", sharePointResponses.data);
              
              // Try different extraction methods based on data structure
              let extractedAnswers = {};
              
              // Method 1: Direct extraction
              extractedAnswers = extractAnswersFromSection(sharePointResponses.data);
              console.log("Method 1 - Direct extraction:", extractedAnswers);
              
              // Method 2: If data has a 'responses' property, extract from there
              if (sharePointResponses.data.responses) {
                const responsesExtracted = extractAnswersFromSection(sharePointResponses.data.responses);
                console.log("Method 2 - From responses property:", responsesExtracted);
                if (Object.keys(responsesExtracted).length > 0) {
                  extractedAnswers = responsesExtracted;
                }
              }
              
              // Method 3: Manual extraction for specific format
              if (sharePointResponses.data.responses) {
                const manualAnswers: Record<string, any> = {};
                Object.keys(sharePointResponses.data.responses).forEach(key => {
                  const response = sharePointResponses.data.responses[key];
                  if (response.selectedOption) {
                    manualAnswers[key] = response.selectedOption;
                  } else if (response.textResponse && response.textResponse.plainText) {
                    manualAnswers[key] = response.textResponse.plainText;
                  }
                });
                console.log("Method 3 - Manual extraction:", manualAnswers);
                if (Object.keys(manualAnswers).length > 0) {
                  extractedAnswers = manualAnswers;
                }
              }
              
              console.log("Final extracted answers:", extractedAnswers);
              logger.debug("Extracted answers from SharePoint:", extractedAnswers);
              Object.assign(answers, extractedAnswers);
            } else {
              console.log("SharePoint loading failed or returned no data");
            }
          } else {
            console.log("planningAPI.loadResponses not available");
          }
        } catch (sharePointError) {
          console.error("SharePoint loading error:", sharePointError);
          logger.warn("Failed to load Engagement Acceptance from SharePoint for cross-section answers", {
            error: sharePointError,
          });
          
          // Fallback: Try to load from local storage
          try {
            console.log("Attempting fallback to local storage...");
            const localData = await (window as any).planning.readSection(companyName, "engagement-acceptance");
            if (localData) {
              const fallbackAnswers = extractAnswersFromSection(localData);
              console.log("Fallback answers from local storage:", fallbackAnswers);
              Object.assign(answers, fallbackAnswers);
            }
          } catch (fallbackError) {
            console.error("Fallback to local storage also failed:", fallbackError);
          }
        }

        setCrossSectionAnswers(answers);
        logger.debug("Loaded cross-section answers", { 
          answers, 
          ep1Value: answers["EP-1"],
          allKeys: Object.keys(answers)
        });
        console.log("Cross-section answers loaded:", answers);
        console.log("EP-1 value:", answers["EP-1"]);
      } catch (error) {
        logger.warn("Failed to load cross-section answers", { error });
      }
    };

    loadMaterialityData();
    loadCrossSectionAnswers();
  }, [companyName]);

  const handlePlanningDataChange = async (data: any) => {
    setPlanningData(data);
    await saveAllData({
      planningData: data,
      revisionData,
      abcotdData,
      componentData,
      documentationData,
    });
  };

  const handleRevisionComplete = () => {
    // Refresh data and switch back to planning tab
    setActiveTab("planning");
  };

  const handleAbcotdDataChange = async (data: any) => {
    setAbcotdData(data);
    await saveAllData({
      planningData,
      revisionData,
      abcotdData: data,
      componentData,
      documentationData,
    });
  };

  const handleComponentDataChange = async (data: any) => {
    setComponentData(data);
    await saveAllData({
      planningData,
      revisionData,
      abcotdData,
      componentData: data,
      documentationData,
    });
  };

  const handleDocumentationDataChange = async (data: any) => {
    setDocumentationData(data);
    await saveAllData({
      planningData,
      revisionData,
      abcotdData,
      componentData,
      documentationData: data,
    });
  };

  const saveAllData = async (materialityData: any) => {
    try {
      await (window as any).planning.saveSection(
        companyName,
        "materiality",
        materialityData,
      );
      setLastSaved(new Date());
    } catch (error) {
      logger.error("Failed to save materiality data", { error });
    }
  };

  const isPlanningComplete =
    planningData &&
    planningData.selectedBenchmarks?.length > 0 &&
    planningData.determinedMateriality > 0;

  return {
    planningData,
    revisionData,
    abcotdData,
    componentData,
    documentationData,
    activeTab,
    lastSaved,
    crossSectionAnswers,
    handlePlanningDataChange,
    handleRevisionComplete,
    handleAbcotdDataChange,
    handleComponentDataChange,
    handleDocumentationDataChange,
    setActiveTab,
    isPlanningComplete,
  };
}
